import { useState, useEffect, useCallback, useMemo } from "react";
import { useSetRecoilState } from "recoil";

import { InvestmentProposal } from "@/api/generated";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import { investmentUnreadCount } from "@/view_models/proposalUnreadCounts";

interface UseGetInvestmentProposals {
  investmentProposals: InvestmentProposal[];
  loading: boolean;
  loadError: boolean;
  fetchInvestmentProposals: () => Promise<void>;
}

export const useGetInvestmentProposals = (): UseGetInvestmentProposals => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [investmentProposals, setInvestmentProposals] = useState<InvestmentProposal[]>([]);

  const fp = useMemo(() => PropallyApiFactory(), []);
  const setInvestmentUnread = useSetRecoilState(investmentUnreadCount);

  const fetchInvestmentProposals = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const data = await fp.v1InvestmentProposalsGet({ withCredentials: true });
      // 想定外の200応答も取得失敗とし、既存の一覧・未読件数へ混入させない。
      if (
        data.status !== 200 ||
        !Array.isArray(data.data) ||
        data.data.some((proposal) => !proposal || typeof proposal !== "object" || Array.isArray(proposal))
      ) {
        throw new Error("Invalid response from /v1/investment_proposals: expected a proposal array");
      }
      const proposals = data.data;
      const unread = proposals.filter((proposal) => !proposal.is_user_read).length;
      setInvestmentProposals(proposals);
      setInvestmentUnread({ unread_count: unread });
    } catch (e) {
      setLoadError(true);
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [fp, setInvestmentUnread]);

  useEffect(() => {
    fetchInvestmentProposals();
  }, []);

  return { investmentProposals, loading, loadError, fetchInvestmentProposals };
};
